import React, { useRef, useEffect } from 'react';
import GenericTable, { TableColumn } from './GenericTable';
import FlexboxContainer from '../FlexboxContainer';
import SearchBar from '../Search';
import Pagination from '../Pagination';
import Switch from '../Switch';
import Select from '../Select';
import MatTableColumnsButton from 'components/MaterialTable/MatTableColumnsButton';
import { Background } from 'styles/themeColors';
import { Space } from 'styles/themeSpaces';
import { BorderRadius } from 'styles/themeBorderRadius';

interface CustomSearch {
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
}

interface CustomPagination {
  itemsPerPage: number;
  setItemsPerPage: (size: number) => void;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  totalPages: number;
  baseRoute: string;
  shouldRedirect?: boolean;
}

interface Props {
  columns: any[];
  data: any[];
  isLoading?: boolean;
  onRowClick?: (event: React.MouseEvent, rowData: any) => void;
  wrapperWidth?: string;
  toolbar?: boolean;
  overrideToolbar?: boolean;
  onOrderChange?: (index: number, direction: string) => void;
  detailPanel?: any[];
  borderBetweenRows?: boolean;
  switchers?: any[];
  customSearch?: CustomSearch;
  selectors?: any[];
  leftSelectors?: { label: string; value: string; options: any[]; onChange: (e: any) => void; width?: string }[];
  tableRef?: any;
  disableBorderCollapse?: boolean;
  customPagination?: CustomPagination;
  sortBy?: any;
  initialColumns?: any[];
  onUpdateColumns?: (columns: any[]) => void;
  columnsForSettings?: any[];
  maxHeight?: string;
  enableSelection?: boolean;
  selectedRows?: any[];
  onSelectionChange?: (selectedRows: any[]) => void;
  isRowSelectable?: (rowData: any) => boolean;
}

const GenericTableWrapper = ({
  columns,
  data,
  isLoading,
  onRowClick,
  wrapperWidth = '100%',
  customSearch,
  customPagination,
  detailPanel,
  onOrderChange,
  maxHeight,
  ...otherProps
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (otherProps.tableRef) {
      otherProps.tableRef.current = { tableContainerDiv: containerRef };
    }
  }, [otherProps.tableRef]);
  // Convert MatDataGrid columns to GenericTable columns
  const genericColumns: TableColumn[] = columns.map(col => ({
    title: col.title,
    field: col.field,
    width: col.width,
    headerStyle: col.headerStyle,
    cellStyle: col.cellStyle,
    render: col.render
  }));

  // Create toolbar content exactly like MatDataGrid
  const searchBar = customSearch ? (
    <SearchBar
      placeholder={customSearch.placeholder}
      searchValue={customSearch.value}
      onSearch={customSearch.onChange}
      containerWidth='320px'
    />
  ) : null;

  const switchComponent = otherProps.switchers?.map((switcher: any, index: number) => (
    <FlexboxContainer key={index} flexDirection="row" alignItems="center" gap="8px">
      <Switch
        id={`switcher-${index}`}
        checked={switcher.checked}
        onChange={switcher.onChange}
        label={switcher.label}
      />
    </FlexboxContainer>
  ));

  const selectComponent = otherProps.selectors?.map((selector: any, index: number) => (
    <FlexboxContainer>
      <Select
        key={index}
        value={selector.value}
        onChange={selector.onChange}
        options={selector.options}
        placeholder={selector.placeholder}
        width="220px !important"
      />
    </FlexboxContainer>
  ));

  // For column settings, we need to pass the original unfiltered columns
  // so that hidden columns still appear in the settings menu
  const columnsForSettings = otherProps.columnsForSettings || columns;

  const tableColumnsButton = otherProps.initialColumns && otherProps.onUpdateColumns ? (
    <MatTableColumnsButton
      columns={columnsForSettings as any[]}
      onUpdateColumns={otherProps.onUpdateColumns}
      initialColumns={otherProps.initialColumns as any[]}
    />
  ) : null;

  const leftSelectorComponent = otherProps.leftSelectors?.map((selector: any, index: number) => (
    <FlexboxContainer key={index} alignItems="center" gap="8px">
      <span className="base" style={{ whiteSpace: 'nowrap' }}>{selector.label}</span>
      <Select
        value={selector.value}
        onChange={selector.onChange}
        options={selector.options}
        width={selector.width || '220px'}
      />
    </FlexboxContainer>
  ));

  const customToolbarContent = (
    <FlexboxContainer
      justifyContent={customSearch ? 'space-between' : 'flex-end'}
      padding={`${Space.spBase} ${Space.sp2x}`}
      alignItems="center"
      bgColor={Background.shaded}
      borderRadius={`${BorderRadius.m} ${BorderRadius.m} 0 0`}
    >
      <FlexboxContainer alignItems="center" gap="16px">
        {searchBar}
        {leftSelectorComponent}
      </FlexboxContainer>
      <FlexboxContainer
        justifyContent="flex-end"
        alignItems="center"
        gap="32px"
      >
        {switchComponent}
        {selectComponent}
        {tableColumnsButton}
      </FlexboxContainer>
    </FlexboxContainer>
  );

  return (
    <div ref={containerRef} style={{ width: wrapperWidth, overflowX: 'auto', containerType: 'inline-size' }}>
      <div style={{ width: '100%', minWidth: 'max-content' }}>
        {/* Custom toolbar like MatDataGrid */}
        {(customSearch || otherProps.switchers || otherProps.selectors || otherProps.leftSelectors) && (
          <div style={{ position: 'sticky', left: 0 }}>
            {customToolbarContent}
          </div>
        )}

        {/* Table starts here */}
        <GenericTable
          data={data}
          columns={genericColumns}
          onRowClick={onRowClick}
          currentPage={customPagination?.currentPage || 1}
          itemsPerPage={customPagination?.itemsPerPage || 50}
          isLoading={isLoading}
          detailPanel={detailPanel}
          onSortChange={onOrderChange}
          sortBy={otherProps.sortBy}
          maxHeight={maxHeight}
          enableSelection={otherProps.enableSelection}
          selectedRows={otherProps.selectedRows}
          onSelectionChange={otherProps.onSelectionChange}
          isRowSelectable={otherProps.isRowSelectable}
        />

        {customPagination && (
          <div style={{ marginTop: '20px' }}>
            <Pagination
              currentPage={customPagination.currentPage}
              setCurrentPage={customPagination.setCurrentPage}
              totalPages={customPagination.totalPages}
              itemsPerPage={customPagination.itemsPerPage}
              setItemsPerPage={customPagination.setItemsPerPage}
              baseRoute={customPagination.baseRoute}
              shouldRedirect={customPagination.shouldRedirect}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default GenericTableWrapper;